import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { RefreshReactQuery } from "../../constants/AppConstants";
import {
  DATA_SAVED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../constants/Dictionary";
import CmSheetFeedbacksCollaboratorsService from "../../services/api/collaborators/CmSheetFeedbacksCollaborators.service";
import { UpdateCmSheetFeedbacksCollaboratorRequest } from "../../types/api/collaborators.type";
import { useNotification } from "../notification.hook";
import { CM_SHEET_STATE_MACHINE_QUERY_KEY } from "./stateMachine.hook";

export const CM_SHEET_COLLABORATOR_FEEDBACKS_QUERY_KEY =
  "get-cm-sheet-collaborator-feedbacks";

export const useCmSheetCollaboratorFeedbacks = (
  cmSheetId: number,
  enabled = true,
) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: [CM_SHEET_COLLABORATOR_FEEDBACKS_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetFeedbacksCollaboratorsService.get(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching, refetch };
};

export const useUpdateCmSheetCollaboratorFeedbacks = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateCollaboratorFeedbacks, isPending: isUpdating } =
    useMutation({
      mutationFn: (body: UpdateCmSheetFeedbacksCollaboratorRequest) =>
        CmSheetFeedbacksCollaboratorsService.update(cmSheetId, body),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [CM_SHEET_COLLABORATOR_FEEDBACKS_QUERY_KEY],
          }),
          queryClient.invalidateQueries({
            queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY],
          }),
        ]);
        notification.success(DATA_SAVED_SUCCESS_LABEL, undefined, "toast");
      },
      onError: (error) => {
        console.error(error);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      },
    });

  return { updateCollaboratorFeedbacks, isUpdating };
};
